/**
 * API Client Wrapper
 * Centralized fetch wrapper for all API calls to /api/* endpoints
 *
 * Features:
 * - Automatic token handling (from HTTP-only cookies)
 * - Comprehensive error handling (timeout, network, server errors)
 * - Retry logic for rate limiting (429) and server errors (502, 503)
 * - Timeout handling (30 seconds default)
 * - Specific error messages by status code
 * - JSON parsing
 * - Proper header configuration
 * - CORS support
 *
 * Usage:
 * import api from '@/lib/api'
 *
 * const user = await api.post('/auth/register', { username, email, password })
 * const users = await api.get('/users')
 * await api.put('/users/:id', { email: 'new@example.com' })
 * await api.delete('/keys/:id')
 */

const BASE_URL = typeof window !== 'undefined'
  ? (process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:20129')
  : 'http://localhost:20129'

// Configuration constants
const TIMEOUT_MS = 30000 // 30 seconds
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

// Status codes that should trigger a retry
const RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504]

/**
 * Map HTTP status codes to user-friendly error messages
 */
const ERROR_MESSAGES = {
  400: 'Invalid request - please check your input',
  401: 'Unauthorized - please login again',
  403: 'Forbidden - you don\'t have permission',
  404: 'Not found - the resource doesn\'t exist',
  408: 'Request timeout - please try again',
  429: 'Too many requests - please wait before trying again',
  500: 'Server error - please try again later',
  502: 'Bad gateway - the server is temporarily unavailable',
  503: 'Service unavailable - please try again later',
  504: 'Gateway timeout - please try again later',
}

/**
 * Parse error response from backend
 * Extracts meaningful error message from response
 * @param {Response} response - Fetch response object
 * @returns {Error} Error object with status, message, and metadata
 */
async function parseError(response) {
  const contentType = response.headers.get('content-type')
  let data = {}

  if (contentType && contentType.includes('application/json')) {
    try {
      data = await response.json()
    } catch (e) {
      // Failed to parse JSON, use generic message
    }
  }

  // Get user-friendly message from map, or fall back to data.error
  const defaultMessage = ERROR_MESSAGES[response.status] || `HTTP ${response.status}`
  const message = data.error || data.message || defaultMessage

  const error = new Error(message)
  error.status = response.status
  error.data = data
  error.isRetryable = RETRYABLE_STATUS_CODES.includes(response.status)

  // Parse Retry-After header for rate limiting
  const retryAfter = response.headers.get('Retry-After')
  if (retryAfter) {
    error.retryAfter = parseInt(retryAfter) * 1000 // Convert to milliseconds
  }

  return error
}

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Create abort controller with timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {AbortController}
 */
function createTimeoutController(timeoutMs) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  // Store timeout ID to clear it later
  controller._timeoutId = timeoutId

  return controller
}

/**
 * Generic fetch wrapper with retry logic and timeout
 * Handles all HTTP methods with automatic token and error handling
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @param {number} retryCount - Current retry attempt (internal)
 * @returns {Promise} Parsed JSON response
 */
async function fetchApi(endpoint, options = {}, retryCount = 0) {
  // Build URL
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`

  // Get auth token from localStorage
  const authHeaders = {}
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('auth_token')
    if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  // Default options — merge headers carefully, don't let spread overwrite
  const fetchOptions = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
    credentials: 'include',
  }

  // Add body if provided (stringify objects)
  if (options.body) {
    if (typeof options.body === 'string') {
      fetchOptions.body = options.body
    } else {
      fetchOptions.body = JSON.stringify(options.body)
    }
  }

  // Add abort controller for timeout
  const controller = createTimeoutController(TIMEOUT_MS)
  fetchOptions.signal = controller.signal

  try {
    const response = await fetch(url, fetchOptions)

    // Clear timeout since request completed
    clearTimeout(controller._timeoutId)

    // Check for successful response
    if (!response.ok) {
      const error = await parseError(response)

      // Special handling for different status codes
      if (response.status === 401) {
        // Unauthorized - token might be expired
        error.shouldRedirectToLogin = true
      } else if (response.status === 403) {
        error.isForbidden = true
      }

      // Retry logic for retryable status codes
      if (error.isRetryable && retryCount < MAX_RETRIES) {
        const delay = error.retryAfter || RETRY_DELAY_MS * Math.pow(2, retryCount)
        await sleep(delay)
        return fetchApi(endpoint, options, retryCount + 1)
      }

      throw error
    }

    // Parse successful response
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    }

    return null
  } catch (error) {
    // Clear timeout on error
    if (controller._timeoutId) {
      clearTimeout(controller._timeoutId)
    }

    // Handle abort error (timeout)
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - the server took too long to respond. Please try again.')
    }

    // Network error or fetch error
    if (error instanceof TypeError) {
      throw new Error('Network error - unable to reach server. Please check your connection.')
    }

    // Re-throw if it's an API error we already processed
    if (error.status) {
      throw error
    }

    // Unknown error
    throw new Error(error.message || 'Unknown error occurred')
  }
}

/**
 * API client methods
 * Provides convenient methods for GET, POST, PUT, DELETE
 */
const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint (e.g., '/api/users')
   * @param {object} options - Optional fetch options
   * @returns {Promise} Parsed JSON response
   */
  get: (endpoint, options = {}) =>
    fetchApi(endpoint, { method: 'GET', ...options }),

  /**
   * POST request
   * @param {string} endpoint - API endpoint (e.g., '/api/auth/login')
   * @param {object} body - Request body (will be JSON stringified)
   * @param {object} options - Optional fetch options
   * @returns {Promise} Parsed JSON response
   */
  post: (endpoint, body = {}, options = {}) =>
    fetchApi(endpoint, { method: 'POST', body, ...options }),

  /**
   * PUT request
   * @param {string} endpoint - API endpoint (e.g., '/api/users/:id')
   * @param {object} body - Request body (will be JSON stringified)
   * @param {object} options - Optional fetch options
   * @returns {Promise} Parsed JSON response
   */
  put: (endpoint, body = {}, options = {}) =>
    fetchApi(endpoint, { method: 'PUT', body, ...options }),

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint (e.g., '/api/keys/:id')
   * @param {object} options - Optional fetch options
   * @returns {Promise} Parsed JSON response
   */
  delete: (endpoint, options = {}) =>
    fetchApi(endpoint, { method: 'DELETE', ...options }),

  /**
   * PATCH request (if needed)
   * @param {string} endpoint - API endpoint
   * @param {object} body - Request body
   * @param {object} options - Optional fetch options
   * @returns {Promise} Parsed JSON response
   */
  patch: (endpoint, body = {}, options = {}) =>
    fetchApi(endpoint, { method: 'PATCH', body, ...options }),
}

export default api
